import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import QRCode from 'qrcode';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD } from '../utils/constants';
import { TICKET_IMPORTANT_INFORMATION } from '../utils/ticketPDF';
import { BoardingPointSchedule, todayIso } from './ScheduledChanges';
import { TicketContent } from './TicketPrint';

// ─── Sample data for live preview ────────────────────────────────────────────
const SAMPLE = {
  booking_name: 'Ramesh Kumar',
  cruise_type:  'Sunset Cruise',
  deck:         'Upper Deck',
  date:         '19-05-2026',
  adults:       2,
  children:     1,
  infants:      0,
  tour_leaders: 0,
};
const SAMPLE_CRUISE = { reporting_time: '17:00', departure_time: '17:30', end_time: '19:30' };
const SAMPLE_REF    = 'SS-001-190526';

// Seed text for an EMPTY ticket_settings row. The seeded boarding line carries
// the {boarding_point} placeholder, which every ticket fills in with the
// boarding point in force on its cruise date — so the wording never has to be
// retyped when boarding moves.
const DEFAULT_SETTINGS = {
  boarding_point:       'Uzanbazar Jahaj Ghat, Guwahati',
  important_information: TICKET_IMPORTANT_INFORMATION.join('\n'),
};

const PREVIEW_SCALE = 0.5;

// ─── Settings form styles ─────────────────────────────────────────────────────
const textareaStyle = (focused) => ({
  width: '100%',
  background: '#F8F6F1',
  border: `1.5px solid ${focused ? GOLD : '#d8d0c0'}`,
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: '0.85rem',
  color: NAVY,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  resize: 'vertical',
  lineHeight: 1.6,
  transition: 'border-color 0.15s',
});

const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: '#5a4a38',
  marginBottom: 6,
};

export default function TicketPreview({ onNavigate }) {
  const [settings, setSettings]         = useState(DEFAULT_SETTINGS);
  const [settingsId, setSettingsId]     = useState(null);
  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]               = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [sampleQR, setSampleQR]         = useState(null);
  const [inForce, setInForce]           = useState(null); // boarding point in force today (boarding_point_on)

  const loadInForce = () => {
    supabase.rpc('boarding_point_on', { p_date: todayIso() }).then(({ data }) => {
      setInForce(Array.isArray(data) && data[0] ? data[0] : null);
    });
  };

  const previewRef    = useRef(null);
  const [previewH, setPreviewH] = useState(null);

  // Load settings from DB and generate sample QR
  useEffect(() => {
    supabase.from('ticket_settings').select('*').limit(1).single().then(({ data }) => {
      if (data) {
        setSettingsId(data.id);
        setSettings({
          boarding_point:        data.boarding_point || DEFAULT_SETTINGS.boarding_point,
          important_information: data.instructions   || DEFAULT_SETTINGS.important_information,
        });
      }
    });
    QRCode.toDataURL(SAMPLE_REF, { width: 200, margin: 1, color: { dark: '#0A1628', light: '#FFFFFF' } })
      .then(setSampleQR)
      .catch(() => {});
    loadInForce();
  }, []);

  // Re-measure preview height when settings content changes
  useLayoutEffect(() => {
    if (previewRef.current) {
      setPreviewH(Math.round(previewRef.current.offsetHeight * PREVIEW_SCALE));
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      boarding_point:       settings.boarding_point,
      instructions:         settings.important_information,
      terms_and_conditions: '',
      updated_at:           new Date().toISOString(),
    };
    if (settingsId) {
      await supabase.from('ticket_settings').update(payload).eq('id', settingsId);
    } else {
      const { data } = await supabase.from('ticket_settings').insert(payload).select('id').single();
      if (data?.id) setSettingsId(data.id);
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('settings')}>← Settings</button>
      </div>

      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Ticket Design</h2>
        <p style={{ fontSize: '0.78rem', color: '#8a7a60' }}>
          Customise boarding pass content. Changes reflect immediately in the preview.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* ── Settings form ── */}
        <div style={{ flex: '0 0 340px', minWidth: 280 }}>
          <div style={{ background: '#fff', border: '1px solid #e8e0d0', borderRadius: 14, padding: '22px 22px 18px', boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 18 }}>
              Ticket Settings
            </div>

            {/* Boarding point — the standing value; a scheduled change overrides it from its date */}
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>Boarding Point</label>
              <textarea
                rows={2}
                value={settings.boarding_point || ''}
                onChange={e => setSettings(p => ({ ...p, boarding_point: e.target.value }))}
                onFocus={() => setFocusedField('boarding_point')}
                onBlur={() => setFocusedField(null)}
                style={textareaStyle(focusedField === 'boarding_point')}
              />
              <p style={{ fontSize: '0.72rem', color: '#8a7a60', margin: '6px 0 0', lineHeight: 1.45 }}>
                {inForce?.scheduled
                  ? <>In force today (scheduled): <strong>{inForce.boarding_point}</strong>. The field above is the standing value.</>
                  : <>Applies at once. To move boarding from a date without touching tickets already issued, schedule a change below.</>}
              </p>
            </div>

            <BoardingPointSchedule current={inForce?.boarding_point || settings.boarding_point} onChanged={loadInForce} />

            {/* Important Information (merged conditions) */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>
                Important Information{' '}
                <span style={{ fontWeight: 400, color: '#8a7a60' }}>(one per line)</span>
              </label>
              <p style={{ fontSize: '0.72rem', color: '#8a7a60', margin: '-2px 0 8px', lineHeight: 1.45 }}>
                Write <code>{'{boarding_point}'}</code> wherever the boarding point belongs. Each ticket fills it in with the point in force on its cruise date.
              </p>
              <textarea
                rows={18}
                value={settings.important_information || ''}
                onChange={e => setSettings(p => ({ ...p, important_information: e.target.value }))}
                onFocus={() => setFocusedField('important_information')}
                onBlur={() => setFocusedField(null)}
                style={textareaStyle(focusedField === 'important_information')}
              />
            </div>

            {saved && (
              <div className="toast toast-success" style={{ marginBottom: 12 }}>Settings saved!</div>
            )}

            <button
              onClick={handleSave}
              disabled={saving}
              style={{ width: '100%', background: GOLD, color: '#fff', border: 'none', borderRadius: 9, padding: '12px 0', fontSize: '0.92rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* ── Live preview ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 }}>
            Live Preview — A4 Portrait
          </div>
          <div style={{ overflowX: 'auto' }}>
            {/* Wrapper clips the scaled ticket to its visual size */}
            <div style={{
              position: 'relative',
              width: `calc(210mm * ${PREVIEW_SCALE})`,
              height: previewH ? `${previewH}px` : 'auto',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(10,22,40,0.18)',
              borderRadius: 4,
            }}>
              <div style={{ transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left', width: '210mm' }}>
                <TicketContent
                  ref={previewRef}
                  booking={SAMPLE}
                  reference_number={SAMPLE_REF}
                  cruise_settings={SAMPLE_CRUISE}
                  ticket_settings={{ ...settings, instructions: settings.important_information }}
                  qr_code_data_url={sampleQR}
                />
              </div>
            </div>
          </div>
          <p style={{ marginTop: 10, fontSize: '0.72rem', color: '#b0a898' }}>
            Preview uses sample data. Actual tickets include real reference numbers and QR codes.
          </p>
        </div>

      </div>
    </div>
  );
}
